import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { request_initial_register } from '../api/magic_link_client';
import { get_welcome_translations, resolve_locale } from '../i18n/welcome_translations';
import { AuthCard } from '../ui/auth_card';
import { TextField } from '../ui/text_field';

/**
 * /welcome — public self-service request form. Submitting triggers the
 * initial-register email. Always shows a generic confirmation (the backend
 * is anti-enumeration), so the panel never reveals whether the person exists.
 */
export function WelcomeRequestRoute(): ReactNode {
  const t = useMemo(() => get_welcome_translations(resolve_locale(window.location.search)), []);
  const [firstname, set_firstname] = useState('');
  const [lastname, set_lastname] = useState('');
  const [birthdate, set_birthdate] = useState('');
  const [checkin, set_checkin] = useState('');
  const [state, set_state] = useState<'idle' | 'submitting' | 'sent'>('idle');
  const [error, set_error] = useState<string | null>(null);

  async function on_submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (firstname === '' || lastname === '' || birthdate === '' || checkin === '') {
      set_error(t.error_required_fields);
      return;
    }
    set_error(null);
    set_state('submitting');
    try {
      await request_initial_register({ firstname, lastname, birthdate, checkin });
    } catch {
      // Anti-enumeration: still show the generic sent panel on error.
    }
    set_state('sent');
  }

  if (state === 'sent') {
    return (
      <AuthCard title={t.request_sent_heading}>
        <p className="uf-lead">{t.request_sent_message}</p>
      </AuthCard>
    );
  }

  const is_submitting = state === 'submitting';

  return (
    <AuthCard title={t.request_heading}>
      <p className="uf-lead">{t.request_description}</p>
      <form className="uf-form" onSubmit={on_submit}>
        <TextField
          label={t.firstname_label}
          value={firstname}
          on_change={set_firstname}
          autocomplete="given-name"
          disabled={is_submitting}
        />
        <TextField
          label={t.lastname_label}
          value={lastname}
          on_change={set_lastname}
          autocomplete="family-name"
          disabled={is_submitting}
        />
        <TextField
          label={t.birthdate_label}
          value={birthdate}
          on_change={set_birthdate}
          type="date"
          autocomplete="bday"
          disabled={is_submitting}
        />
        <TextField
          label={t.checkin_label}
          value={checkin}
          on_change={set_checkin}
          disabled={is_submitting}
        />
        {error !== null ? (
          <p className="uf-alert" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="uf-btn" disabled={is_submitting}>
          {is_submitting === true ? t.submitting : t.request_submit}
        </button>
      </form>
    </AuthCard>
  );
}
